import React, { useEffect, useState } from "react";
import { Building2, Users, Eye, EyeOff } from "lucide-react";

import { ValidateInput } from "@/components/SearchBox/ValidateInput";
import {
  validateCompanyName,
  validateEmail,
  validateUsername,
} from "@/utils/validators";

import { useSelector } from "react-redux";
import { APICall } from "@/config/NetworkUtility";
import FormLabel from "@/components/common/FormLabel";
import ImageUploadField from "@/components/common/ImageUploadField";

const StepCompanyInfo = ({ formData, updateFormData, setValidationState }) => {
  const countryList = useSelector((state) => state.common.countryList);

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordError, setPasswordError] = useState("");

  const [stateList, setStateList] = useState([]);

  const [preview, setPreview] = useState(null);

  const fetchStates = async (countryCode) => {
    try {
      const response = await APICall(
        "get",
        {},
        `v1/Common/GetCountryStateList?CountryCode=${countryCode}`,
      );

      setStateList(response?.data || []);
    } catch (err) {
      console.error("State API Error:", err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        formData.CONFIRM_PASSWORD &&
        formData.PROFILE_PASSWORD !== formData.CONFIRM_PASSWORD
      ) {
        setPasswordError("Passwords do not match");
      } else {
        setPasswordError("");
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.PROFILE_PASSWORD, formData.CONFIRM_PASSWORD]);

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const inputBaseClass = `w-full px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-400 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-teal-500`;
  const selectInputClass = `w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-600 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-teal-500 focus:border-blue-500 dark:focus:border-teal-500 transition-all`;

  return (
    <div className="grid lg:grid-cols-2 gap-8 animate-[stepFade_0.35s_ease-out]">
      {/* COMPANY INFO */}
      <div className="bg-white dark:bg-slate-900 border border-slate-800 rounded-2xl p-5">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-400 mb-6 flex items-center gap-2">
          <Building2 className="w-5 h-5 dark:text-teal-500 " />
          Company Information
        </h3>

        <div className="space-y-6">
          {/* BASIC INFO */}
          <div className="space-y-4">
            <div className="border-b border-slate-700 pb-2">
              <h4 className="text-sm font-semibold dark:text-teal-400">
                Basic Information
              </h4>
            </div>

            <div>
              <FormLabel>Company Name</FormLabel>
              <ValidateInput
                value={formData.COMPANY_NAME}
                onChange={(value) => updateFormData("COMPANY_NAME", value)}
                placeholder="Company Name"
                minLength={3}
                debounceTime={600}
                onValidationChange={(res) =>
                  setValidationState((prev) => ({
                    ...prev,
                    COMPANY_NAME: res,
                  }))
                }
                validator={validateCompanyName}
                inputClassName="bg-slate-800 border-slate-600 text-slate-900 dark:text-slate-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormLabel>Company Email</FormLabel>

                <ValidateInput
                  type="email"
                  value={formData.PROFILE_EMAILID}
                  onChange={(value) => updateFormData("PROFILE_EMAILID", value)}
                  placeholder="info@company.com"
                  minLength={5}
                  debounceTime={600}
                  validator={validateEmail}
                  onValidationChange={(res) =>
                    setValidationState((prev) => ({
                      ...prev,
                      PROFILE_EMAILID: res,
                    }))
                  }
                  inputClassName="bg-slate-800 border-slate-600 text-slate-900 dark:text-slate-400"
                />
              </div>

              <div>
                <FormLabel>Phone</FormLabel>

                <input
                  type="tel"
                  value={formData.PROFILE_PHONE}
                  onChange={(e) =>
                    updateFormData("PROFILE_PHONE", e.target.value)
                  }
                  placeholder="9876543210"
                  className={inputBaseClass}
                />
              </div>
            </div>
            {/* NVOCC LICENSE */}
            {formData.PROFILE_CATEGORY === "NVOCC" && (
              <div>
                <FormLabel>NVOCC License</FormLabel>

                <input
                  type="text"
                  value={formData.LICENCE_NUMBER}
                  onChange={(e) =>
                    updateFormData("LICENCE_NUMBER", e.target.value)
                  }
                  placeholder="Enter NVOCC License Number"
                  className={inputBaseClass}
                />
              </div>
            )}
          </div>

          {/* ADDRESS */}
          <div className="space-y-4">
            <div className="border-b border-slate-700 pb-2">
              <h4 className="text-sm font-semibold dark:text-teal-400">
                Address Details
              </h4>
            </div>

            <input
              type="text"
              value={formData.PROFILE_ADDRESS1}
              onChange={(e) =>
                updateFormData("PROFILE_ADDRESS1", e.target.value)
              }
              placeholder="Address Line 1"
              className={inputBaseClass}
            />

            <input
              type="text"
              value={formData.PROFILE_ADDRESS2}
              onChange={(e) =>
                updateFormData("PROFILE_ADDRESS2", e.target.value)
              }
              placeholder="Address Line 2"
              className={inputBaseClass}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormLabel>Country</FormLabel>

                <select
                  value={formData.PROFILE_COUNTRY}
                  onChange={(e) => {
                    const value = e.target.value;

                    const match = value.match(/\((.*?)\)/);

                    const countryCode = match?.[1] || "";

                    updateFormData("PROFILE_COUNTRY", value);

                    updateFormData("PROFILE_COUNTRY_CODE", countryCode);

                    fetchStates(countryCode);
                  }}
                  className={selectInputClass}
                >
                  <option value="">Select Country</option>

                  {countryList?.map((country) => (
                    <option key={country.id} value={country.incoterm}>
                      {country.incoterm}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <FormLabel>State</FormLabel>

                <select
                  value={formData.PROFILE_REGION}
                  onChange={(e) =>
                    updateFormData("PROFILE_REGION", e.target.value)
                  }
                  className={selectInputClass}
                >
                  <option value="">Select State</option>

                  {stateList?.map((state) => (
                    <option key={state.id} value={state.stateName}>
                      {state.stateName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={formData.PROFILE_CITY}
                onChange={(e) => updateFormData("PROFILE_CITY", e.target.value)}
                placeholder="City"
                className={inputBaseClass}
              />

              <input
                type="text"
                value={formData.PROFILE_ZIPCODE}
                onChange={(e) =>
                  updateFormData("PROFILE_ZIPCODE", e.target.value)
                }
                placeholder="Zip Code"
                className={inputBaseClass}
              />
            </div>
          </div>

          {/* BRANDING */}
          <div className="space-y-4">
            <div className="border-b border-slate-200 dark:border-slate-700 pb-2">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-teal-400">
                Branding
              </h4>
            </div>

            <ImageUploadField
              label="Company Logo"
              value={preview}
              maxSize={5}
              accept={["image/png", "image/jpeg", "image/jpg", "image/webp"]}
              hint="PNG, JPG, WEBP • Max 5MB"
              onChange={(base64, file) => {
                updateFormData("FILE_DATA", file);

                if (file) {
                  const previewUrl = URL.createObjectURL(file);

                  setPreview(previewUrl);
                } else {
                  setPreview(null);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* ADMIN ACCOUNT */}
      <div className="bg-white dark:bg-slate-900 border border-slate-800 rounded-2xl p-5">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-400 mb-6 flex items-center gap-2">
          <Users className="w-5 h-5 dark:text-teal-500" />
          Admin Account
        </h3>

        <div className="space-y-6">
          {/* PERSONAL INFO */}
          <div className="space-y-4">
            <div className="border-b border-slate-700 pb-2">
              <h4 className="text-sm font-semibold dark:text-teal-400">
                Personal Information
              </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={formData.PROFILE_FIRSTNAME}
                onChange={(e) =>
                  updateFormData("PROFILE_FIRSTNAME", e.target.value)
                }
                placeholder="First Name"
                className={inputBaseClass}
              />

              <input
                type="text"
                value={formData.PROFILE_LASTNAME}
                onChange={(e) =>
                  updateFormData("PROFILE_LASTNAME", e.target.value)
                }
                placeholder="Last Name"
                className={inputBaseClass}
              />
            </div>
          </div>

          {/* LOGIN */}
          <div className="space-y-4">
            <div className="border-b border-slate-700 pb-2">
              <h4 className="text-sm font-semibold dark:text-teal-400">
                Login Credentials
              </h4>
            </div>

            <div>
              <FormLabel>Username</FormLabel>
              <ValidateInput
                value={formData.PROFILE_USERNAME}
                onChange={(value) => updateFormData("PROFILE_USERNAME", value)}
                placeholder="Username"
                minLength={4}
                debounceTime={600}
                validator={validateUsername}
                onValidationChange={(res) =>
                  setValidationState((prev) => ({
                    ...prev,
                    PROFILE_USERNAME: res,
                  }))
                }
                inputClassName="bg-slate-800 border-slate-600 text-white"
              />
            </div>

            {/* PASSWORD */}
            <div>
              <FormLabel>Password</FormLabel>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.PROFILE_PASSWORD}
                  onChange={(e) =>
                    updateFormData("PROFILE_PASSWORD", e.target.value)
                  }
                  autoComplete="off"
                  className="w-full px-3 py-2 pr-10 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-900 dark:text-slate-400"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* CONFIRM PASSWORD */}
            <div>
              <FormLabel>Confirm Password</FormLabel>

              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.CONFIRM_PASSWORD}
                  onChange={(e) =>
                    updateFormData("CONFIRM_PASSWORD", e.target.value)
                  }
                  className={`${inputBaseClass}
                                    ${
                                      passwordError
                                        ? "border-red-500"
                                        : "border-slate-600"
                                    }`}
                />

                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-900 dark:text-slate-400"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>

              {passwordError && (
                <p className="text-red-400 text-xs mt-1">{passwordError}</p>
              )}
            </div>

            {/* STATUS */}
            <div>
              <FormLabel>Initial Status</FormLabel>

              <select
                value={formData.subscription_status}
                onChange={(e) =>
                  updateFormData("subscription_status", e.target.value)
                }
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-600 rounded-lg text-slate-900 dark:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-teal-500"
              >
                <option value="active">Active</option>

                <option value="trial">Trial</option>

                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepCompanyInfo;
